import { useUserIdentity } from '@src/stores-v2';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useEffect } from 'react';

/**
 * Programmatically shows the Pylon chat bubble when the corresponding
 * LaunchDarkly flag is enabled. Safe to call multiple times.
 */
export function usePylonChatBubble() {
  const identity = useUserIdentity();
  const { enablePylonChatBubble } = useFlagsCombination(identity.currentRole);

  useEffect(() => {
    if (!enablePylonChatBubble) return;
    const logger = LogManager.getLogger('pylon:chatBubble');
    // Pylon snippet defines window.Pylon as a function queue until script loads
    const maybePylon = window.Pylon;
    if (typeof maybePylon === 'function') {
      // Avoid injecting multiple identical widget scripts
      const existing = document.querySelector('script[data-pylon-chat-bubble]');
      if (existing) {
        return;
      }
      try {
        overridePylonChatWidgetStyles();
      } catch (e) {
        logger.warn('Failed to override Pylon chat bubble styles', e);
      }
      try {
        var s = document.createElement('script');
        s.setAttribute('type', 'text/javascript');
        s.setAttribute('async', 'true');
        s.setAttribute(
          'src',
          `https://widget.usepylon.com/widget/${process.env.REACT_APP_PYLON_APP_ID}`,
        );
        s.setAttribute('data-pylon-chat-bubble', 'true');
        document.body.appendChild(s);
      } catch (e) {
        logger.warn('Failed to trigger Pylon chat bubble', e);
      }
    } else {
      logger.debug(
        'Pylon not yet available on window; will retry on next render',
      );
    }
  }, [enablePylonChatBubble]);
}

/**
 * Docs: https://support.usepylon.com/articles/4033853157-left-align-the-chat-window
 */
function overridePylonChatWidgetStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
  .PylonChat-bubbleFrameContainer {
    right: 24px !important;
    bottom: 16px !important;
  }
  .PylonChat-chatWindowFrameContainer {
    right: 24px !important;
    bottom: 80px !important;
  }
`;
  document.head.appendChild(style);
}
